// The one shell. Every sign-in, every screen size.
//
// The header, the five places, Scan and Raise are the same on a phone and a
// tablet; what changes at 700px is where they sit. Under it the tabs run along
// the bottom, Scan and the raise kinds fan out of the floating + above them,
// and everything is one column. At or above it the tabs stand in a rail on
// the left with Raise at its foot, Scan, the scope chips and the score move
// up into the header, and a second rail on the right carries what a phone
// tucks into sheets. There is no separate board any more: the 1000px station
// board never fitted the handheld tablets the floor actually carries, and a
// leader's wall board was a second app to keep in step with the first.
//
// A leader is the same shell with a different Home: the floor, in three views,
// until they drill into a station — at which point they are looking at exactly
// what the operator there is looking at, with a way back at the top.
//
// This owns the pod in hand, the scanner, the job flow and every sheet, which
// used to be split three ways between the operator, leader and board shells
// and drifted apart every time one of them was fixed.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api } from '@/api/client';
import { andonApi } from '@/api/andon/client';
import type { JourneyStepVM, NeedsYouVM, PodVM, RaisedAlertVM } from '@/api/types';
import { PlusFab } from '@/components/home/PlusFab';
import { PushBanner } from '@/components/ui/PushBanner';
import { useAndonLive } from '@/hooks/useAndonLive';
import { showFab, type FabItemKey } from '@/lib/fab';
import { tabBadges } from '@/lib/tabs';
import { registerPush, watchPushHealth } from '@/pwa';
import { Banner } from '@/components/ui/Banner';
import { Button } from '@/components/ui/Button';
import { PodMark } from '@/components/ui/Icon';
import { OfflinePill } from '@/components/ui/OfflinePill';
import { Sheet } from '@/components/ui/Sheet';
import { ReasonList, SectionLabel } from '@/components/ui/misc';
import { TabBar } from '@/components/home/TabBar';
import { AppHeader } from '@/components/shell/AppHeader';
import { RightRail } from '@/components/shell/RightRail';
import { SideRail } from '@/components/shell/SideRail';
import { Toast } from '@/components/shell/Toast';
import { CameraScanner } from '@/components/pod/CameraScanner';
import { PodDocs } from '@/components/pod/PodDocs';
import { useT } from '@/i18n/useT';
import { downscaleToJpeg } from '@/lib/image';
import { milestoneFor, todayKey, type Milestone } from '@/lib/milestone';
import { isTooQuick, useElapsedSeconds } from '@/hooks/useElapsed';
import { useHardwareScanner } from '@/hooks/useHardwareScanner';
import {
  keys,
  useCheckAction,
  useChecks,
  useCompletedToday,
  useCrewToday,
  useFloorScopeFlowlines,
  useJourney,
  useLossReasons,
  useMyOwnedAndons,
  useNeedsYou,
  useQueue,
  useScopeFlowlines,
  useScore,
  useStationAndons,
  useStationBuffers,
  useWoLifecycle,
  useWoLive,
  andonHref,
} from '@/queries';
import { useFloorNeeds, useFloorScore } from '@/queries/leader';
import { useStore } from '@/store';
import { isTablet } from '@/store/slices/ui';
import { HomeScreen, type NeedActions } from './phone/HomeScreen';
import { JobScreen } from './phone/JobScreen';
import { PodsScreen } from './phone/PodsScreen';
import { AlertsScreen } from './phone/AlertsScreen';
import { AndonScreen } from './phone/AndonScreen';
import { RaiseSheetLive } from './phone/raise/AndonRaiseSheet';
import { MoreScreen } from './phone/MoreScreen';
import { IswLibraryScreen } from './phone/IswLibrary';
import { SettingsSheet } from './phone/SettingsSheet';
import { StationPickerSheet } from './phone/StationPicker';
import { PodFinderSheet } from './phone/PodFinder';
import { AlertFormSheet, IssuesSheet, RaisedSheet } from './phone/ReportSheets';
import { AndonHandoffSheet, RaiseSheet, openSafetyIncidentReport, type RaiseKind } from './phone/RaiseSheet';
import { LeaderHome } from './leader/LeaderHome';
import { QualitySheet } from './sheets/QualitySheet';
import { AndonSheet } from './sheets/AndonSheet';
import { CrewSheet } from './sheets/CrewSheet';
import { NotificationSheet } from './sheets/NotificationSheet';
import { AlertScreen } from './alert/AlertScreen';
import { Confetti } from '@/components/andon/Confetti';

/** Reasons offered when finishing suspiciously fast (the spec's list). */
const QUICK_REASONS = [
  'Started earlier — forgot to scan in',
  'Pre-work done last shift',
  'Genuinely a quick one',
  'Correcting a mis-scan',
];

export function Shell() {
  const t = useT();
  const vw = useStore((s) => s.vw);
  const tablet = isTablet(vw);
  const screen = useStore((s) => s.screen);
  const setScreen = useStore((s) => s.setScreen);
  const openWoId = useStore((s) => s.openWoId);
  const openJob = useStore((s) => s.openJob);
  const tab = useStore((s) => s.tab);
  const setTab = useStore((s) => s.setTab);
  const openPods = useStore((s) => s.openPods);
  const isLeader = useStore((s) => s.isLeader);
  const leaderView = useStore((s) => s.leaderView);
  const setLeaderView = useStore((s) => s.setLeaderView);
  const overlay = useStore((s) => s.overlay);
  const openOverlay = useStore((s) => s.openOverlay);
  const closeOverlay = useStore((s) => s.closeOverlay);
  const openQualityAlert = useStore((s) => s.openQualityAlert);
  const openAndon = useStore((s) => s.openAndon);
  const openAlertId = useStore((s) => s.openAlertId);
  const openAndonId = useStore((s) => s.openAndonId);
  const showToast = useStore((s) => s.showToast);
  const activeIndex = useStore((s) => s.activeIndex);
  const setActiveIndex = useStore((s) => s.setActiveIndex);
  const setStarted = useStore((s) => s.setStarted);
  const setPauseReason = useStore((s) => s.setPauseReason);
  const resetJob = useStore((s) => s.resetJob);
  const lastError = useStore((s) => s.lastError);
  const clearError = useStore((s) => s.clearError);
  const showError = useStore((s) => s.showError);
  const scanning = useStore((s) => s.scanning);
  const setScanning = useStore((s) => s.setScanning);
  const workcentreId = useStore((s) => s.workcentreId);
  const flowlineId = useStore((s) => s.flowlineId);
  const wcName = useStore((s) => s.workcentreName);
  const setStation = useStore((s) => s.setStation);
  const operator = useStore((s) => s.operator);
  const milestoneShownOn = useStore((s) => s.milestoneShownOn);
  const markMilestoneShown = useStore((s) => s.markMilestoneShown);
  // The andon module's switch. The navigation is the same for every session;
  // what this decides is what the andon surfaces reach — the raise flow and
  // the thread, pushes and the live stream, a leader's "Take it" — or, off,
  // the Andon-app hand-off and the read-only sheet the app always had.
  const andonPreview = useStore((s) => s.andonCaps.preview);
  const openRaise = useStore((s) => s.openRaise);
  const startRaise = useStore((s) => s.startRaise);
  const setDraftPod = useStore((s) => s.setDraftPod);
  const celebrate = useStore((s) => s.celebrate);

  const [quickReason, setQuickReason] = useState<string | null>(null);
  const [claimedStart, setClaimedStart] = useState('');
  const [photoCheckId, setPhotoCheckId] = useState<number | null>(null);
  const [pendingPhoto, setPendingPhoto] = useState<{ checkId: number; base64: string } | null>(null);
  const [raised, setRaised] = useState<RaisedAlertVM | null>(null);
  const [issuesView, setIssuesView] = useState<'pod' | 'mine'>('pod');
  // A quality sheet opened from the pod's issues list goes back to that list
  // when it closes, so the operator can walk the pod's alerts one by one.
  const [qualityFrom, setQualityFrom] = useState<'issues' | null>(null);
  const [justDone, setJustDone] = useState<{ mo: string; proj: string; nextStep: string | null } | null>(null);
  const [milestone, setMilestone] = useState<Milestone | null>(null);
  /**
   * A pod opened from the finder that no station queue carries. The finder
   * searches every pod in any state, and the queue is a much narrower list, so
   * holding the searched row is what lets a done or held pod be opened at all.
   */
  const [foundPod, setFoundPod] = useState<PodVM | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const qc = useQueryClient();

  // Where a leader is: on the floor, or drilled into a station.
  const onFloor = isLeader && leaderView === 'floor';
  const drilled = isLeader && leaderView === 'station';

  const queue = useQueue();
  const pods = queue.data ?? [];
  const queued = pods[Math.min(activeIndex, Math.max(0, pods.length - 1))];
  // The queue's own row wins the moment one turns up.
  const pod = foundPod ? (pods.find((p) => p.woId === foundPod.woId) ?? foundPod) : queued;

  /** The queue at a station, read fresh — see the finder's reasoning. */
  const queueAt = (wc: number, fl: number | null) =>
    qc.fetchQuery({ queryKey: keys.queue(wc, fl), queryFn: () => api.queue(wc, fl) });

  // ── restoring a /job/<woId> URL ────────────────────────────────────────────
  const [lookupWoId, setLookupWoId] = useState<number | null>(null);
  const restoredRef = useRef<number | null>(null);
  const restoring =
    screen === 'job' &&
    openWoId != null &&
    pod?.woId !== openWoId &&
    (restoredRef.current !== openWoId || lookupWoId === openWoId);

  useEffect(() => {
    if (screen !== 'job' || openWoId == null) {
      restoredRef.current = null;
      return;
    }
    if (pod?.woId === openWoId) return;
    if (restoredRef.current === openWoId) return;
    if (workcentreId != null && queue.isLoading) return;
    restoredRef.current = openWoId;

    const at = pods.findIndex((p) => p.woId === openWoId);
    if (at >= 0) {
      setFoundPod(null);
      setActiveIndex(at);
      return;
    }
    setLookupWoId(openWoId);
    void api
      .podByWo(openWoId)
      .then((found) => {
        if (found) {
          setFoundPod(found);
          return;
        }
        showError('That pod is no longer available.');
        setScreen('home');
      })
      .catch((e) => {
        showError((e as Error).message);
        setScreen('home');
      })
      .finally(() => setLookupWoId(null));
  }, [screen, openWoId, pod?.woId, pods, queue.isLoading, workcentreId, setActiveIndex, setScreen, showError]);

  // ── the job in hand ────────────────────────────────────────────────────────
  const live = useWoLive(pod?.woId ?? null);
  const checks = useChecks(pod?.woId ?? null);
  const elapsed = useElapsedSeconds(live.data);
  const lifecycle = useWoLifecycle(pod?.woId ?? null, pod?.moId ?? null);
  const lossReasons = useLossReasons();
  const checkAction = useCheckAction(pod?.woId ?? null);
  const journey = useJourney(pod?.moId ?? null);
  const completed = useCompletedToday();

  const showingJob = screen === 'job' && !!pod && !restoring;
  // A scan means something on any tab. It stays off inside a job, where a
  // stray trigger pull would yank the operator off the pod they are on.
  const scanEnabled = !showingJob && !restoring && screen !== 'complete' && screen !== 'isw' && screen !== 'raise';

  const onScan = async (code: string) => {
    if (workcentreId == null) {
      showError(`Pick a station before scanning — “${code}” could be anywhere.`);
      return;
    }
    if (scanning) return;
    setScanning(true);
    try {
      const found = await api.findByCode(code, workcentreId);
      if (!found) {
        showError(`“${code}” is not waiting at ${wcName ?? 'this station'}.`);
        return;
      }
      const fresh = await queueAt(workcentreId, flowlineId);
      const at = fresh.findIndex((p) => p.woId === found.woId);
      if (at >= 0) {
        setFoundPod(null);
        setActiveIndex(at);
      } else {
        setFoundPod(found);
      }
      closeOverlay();
      if (isLeader) setLeaderView('station');
      openJob(found.woId);
    } catch (e) {
      showError((e as Error).message);
    } finally {
      setScanning(false);
    }
  };
  useHardwareScanner(scanEnabled, (code) => void onScan(code));

  /** Leader → station: point the app at it and switch view. Scope stays. */
  const goToStation = useCallback(
    (id: number, name: string) => {
      const fl = useStore.getState();
      setStation({ id, name }, fl.flowlineId != null && fl.flowlineName ? { id: fl.flowlineId, name: fl.flowlineName } : null);
      setFoundPod(null);
      setActiveIndex(0);
      if (isLeader) setLeaderView('station');
      else setTab('home');
      showToast(`${t.nowAtT} ${name}`);
    },
    [setStation, setActiveIndex, isLeader, setLeaderView, setTab, showToast, t.nowAtT],
  );

  const pickFound = (found: PodVM) => {
    const here = found.workcentreId === workcentreId;
    if (!here) {
      setStation(
        { id: found.workcentreId, name: found.workcentreName },
        found.flowlineId ? { id: found.flowlineId, name: found.flowlineName ?? '' } : null,
      );
    }
    closeOverlay();
    setFoundPod(found);
    if (isLeader) setLeaderView('station');
    openJob(found.woId);
    void queueAt(found.workcentreId, here ? flowlineId : (found.flowlineId ?? null))
      .then((rows) => {
        const at = rows.findIndex((p) => p.woId === found.woId);
        if (at < 0) return;
        setActiveIndex(at);
        setFoundPod(null);
      })
      .catch(() => {
        /* The searched row still stands on its own. */
      });
  };

  const openPodRow = (row: PodVM) => {
    const at = pods.findIndex((p) => p.woId === row.woId);
    if (at >= 0) {
      setFoundPod(null);
      setActiveIndex(at);
    } else {
      setFoundPod(row);
    }
    openJob(row.woId);
  };

  const jumpToStep = (step: JourneyStepVM) => {
    const here = step.workcentreId === workcentreId;
    if (!here) setStation({ id: step.workcentreId, name: step.workcentreName }, null);
    setFoundPod(null);
    void queueAt(step.workcentreId, here ? flowlineId : null)
      .then((rows) => {
        const at = rows.findIndex((p) => p.woId === step.woId);
        if (at >= 0) {
          setActiveIndex(at);
          openJob(step.woId);
        } else {
          setScreen('home');
          showError(`${step.workcentreName}: ${step.name} is not in that queue.`);
        }
      })
      .catch((e) => {
        setScreen('home');
        showError((e as Error).message);
      });
  };

  const start = () => {
    if (!pod) return;
    setStarted(pod.woId, pod.mo);
    setPauseReason(null);
    lifecycle.start.mutate(undefined, { onError: () => setStarted(null, null) });
  };

  const completeNow = (attestation?: { reason: string; claimedStart?: string }) => {
    const steps = journey.data ?? [];
    const here = steps.findIndex((s) => s.woId === pod?.woId);
    const next = here >= 0 ? steps.slice(here + 1).find((s) => s.status !== 'done') : undefined;
    const done = pod ? { mo: pod.mo, proj: pod.proj, nextStep: next?.workcentreName ?? null } : null;
    lifecycle.finish.mutate(attestation, {
      onSuccess: () => {
        setJustDone(done);
        closeOverlay();
        setScreen('complete');
        if (done) showToast(`${done.mo} ${t.signedOffRecordedT} ${wcName ?? ''}`);
        void completed.refetch().then((r) => {
          const key = todayKey();
          if (milestoneShownOn === key) return;
          const earned = milestoneFor(r.data ?? [], wcName);
          if (!earned) return;
          markMilestoneShown(key);
          setMilestone(earned);
        });
      },
    });
  };

  const tryComplete = () => {
    if (!pod) return;
    if (isTooQuick(elapsed, pod.estMinutes)) {
      setQuickReason(null);
      setClaimedStart('');
      openOverlay('quick');
      return;
    }
    completeNow();
  };

  const onPhotoPicked = async (file: File | undefined) => {
    if (!file || photoCheckId == null) return;
    const check = (checks.data ?? []).find((c) => c.id === photoCheckId);
    if (!check) return;
    const base64 = await downscaleToJpeg(file);
    setPendingPhoto({ checkId: check.id, base64 });
    setPhotoCheckId(null);
  };

  const confirmPhoto = () => {
    if (!pendingPhoto) return;
    const check = (checks.data ?? []).find((c) => c.id === pendingPhoto.checkId);
    if (!check) return;
    // Pending on the card, like every other sign-off, and cleared only when
    // Odoo has answered — but only on this card, so the next check can be
    // signed off while the photo is still on its way up.
    useStore.getState().setCheckPending(check.id, true);
    checkAction.mutate(
      { check, action: { kind: 'photo', base64: pendingPhoto.base64 } },
      { onSuccess: () => setPendingPhoto(null), onSettled: () => useStore.getState().setCheckPending(check.id, false) },
    );
  };

  const nextPod = () => {
    setMilestone(null);
    resetJob();
    setFoundPod(null);
    setActiveIndex(pods.length ? (activeIndex + 1) % pods.length : 0);
    setScreen('home');
    queue.refetch();
  };

  /**
   * The pod an Andon-app hand-off is about. Held here rather than read off
   * the pod in hand because the quality sheet raises on ITS alert's pod,
   * which need not be the one open — and a hand-off carrying the wrong MO is
   * worse than one carrying none.
   */
  const [handoffMoId, setHandoffMoId] = useState<number | null>(null);
  /**
   * Raise an andon about a pod, from wherever the ask came from.
   *
   * With the preview the raise is this app's own sheet, opened on a clean
   * draft carrying the pod, so nothing has to be typed twice. Without it the
   * Andon app owns raising, and the ask hands over to it with the same pod.
   */
  const raiseAndonOn = (moId: number | null, mo: string | null) => {
    if (!andonPreview) {
      setHandoffMoId(moId);
      return openOverlay('andon');
    }
    startRaise();
    setDraftPod(moId, mo);
    openRaise();
  };

  const onChooseRaise = (kind: RaiseKind) => {
    if (kind === 'andon') return raiseAndonOn(pod?.moId ?? null, pod?.mo ?? null);
    if (kind === 'safety') {
      closeOverlay();
      return openSafetyIncidentReport();
    }
    return openOverlay('form');
  };

  /**
   * The floating +'s pills: Scan, and the three raise kinds (frame v2.12).
   *
   * The dial is only on screen while a scan is allowed — it hides on the job
   * screen with the same rule as the wedge scanner — so Scan needs no muting
   * here: a pill that is never drawn mid-pod cannot be tapped mid-pod.
   */
  const [fabOpen, setFabOpen] = useState(false);
  const onChooseFab = (key: FabItemKey) => {
    setFabOpen(false);
    if (key === 'scan') return openOverlay('camera');
    onChooseRaise(key);
  };

  // ── what needs somebody ────────────────────────────────────────────────────
  const openTodo = useCallback(
    (item: NeedsYouVM) => {
      // A to-do lives on whatever record it hangs off, which this app has no
      // screen for. Saying where it is beats opening something that is not it.
      if (item.todo) showError(`${item.todo.summary} — on ${item.todo.about || item.todo.resModel} in Odoo.`);
    },
    [showError],
  );
  const actions = useMemo<NeedActions>(
    () => ({ goToStation, openAndon, openQuality: openQualityAlert, openTodo }),
    [goToStation, openAndon, openQualityAlert, openTodo],
  );
  /** A row of the leader's own list: an andon they own leaves for the Andon app. */
  const openNeedsYouItem = (item: NeedsYouVM) => {
    if (item.kind === 'andon' && item.andon) return openAndon(item.andon.id);
    if (item.kind === 'quality' && item.alert) return openQualityAlert(item.alert.id);
    openTodo(item);
  };

  // ── scope, score, header ───────────────────────────────────────────────────
  const stationScope = useScopeFlowlines();
  const floorScope = useFloorScopeFlowlines();
  const scopeOptions = onFloor ? floorScope : stationScope;
  const scopeIds = useMemo(
    () => (flowlineId == null ? scopeOptions.map((o) => o.id) : [flowlineId]),
    [flowlineId, scopeOptions],
  );
  const scopeLabel = flowlineId == null ? t.allFlowlinesT : (scopeOptions.find((o) => o.id === flowlineId)?.short ?? '');
  const pickScope = (id: number | null) => {
    const wc = workcentreId != null && wcName ? { id: workcentreId, name: wcName } : null;
    const chosen = id == null ? null : scopeOptions.find((o) => o.id === id);
    setStation(wc, chosen ? { id: chosen.id, name: chosen.name } : null);
  };

  const stationScore = useScore();
  const floorScore = useFloorScore();
  const headerScore = onFloor ? floorScore.data : stationScore.data;

  const needs = useNeedsYou();
  const floorNeeds = useFloorNeeds(scopeIds, actions, onFloor);
  // The Andon badge counts what the Andon tab lists: for a leader the
  // unanswered andons and the starved work centres on the floor, for an
  // operator the open andons at their station — the same read the tab draws
  // from. The Quality badge counts the quality alerts alone. See `lib/tabs`
  // for the arithmetic, which is the frame's.
  const stationAndons = useStationAndons(!onFloor);
  const andonCount = onFloor ? floorNeeds.andons.length : (stationAndons.data?.length ?? 0);
  const starvedCount = onFloor ? floorNeeds.starved.length : 0;
  const qualityCount = onFloor ? floorNeeds.quality.length : needs.items.filter((i) => i.kind === 'quality').length;
  const badges = tabBadges({ andons: andonCount, starved: starvedCount, quality: qualityCount });

  // ── the andon module, with the preview on ──────────────────────────────────
  // One stream for the whole app; every andon read re-runs off a nudge from it.
  useAndonLive(andonPreview);
  // Somebody told "notifications are blocked, here is where the switch is"
  // goes to Settings, flips it, and comes back. Nothing in the page changed
  // while they were gone, so without this the banner would still say blocked.
  useEffect(() => (andonPreview ? watchPushHealth() : undefined), [andonPreview]);
  // Register this device for push, for whoever is signed in at it — KEYED ON
  // THE PERSON, NOT THE SESSION. A PIN device's session outlives the operator
  // on it, so registering once at boot would bind the device to whoever stood
  // at it first, for days. The server moves the endpoint to the new person.
  const operatorId = operator?.id ?? null;
  useEffect(() => {
    if (andonPreview && operatorId != null) void registerPush();
  }, [andonPreview, operatorId]);
  // The andon module reads the station off the session for a raise, the way
  // the Andon app did, rather than trusting what travels with the raise. The
  // device remembers the station; this is what makes the server agree.
  useEffect(() => {
    if (!andonPreview || workcentreId == null) return;
    void andonApi.setStation(workcentreId, flowlineId).catch(() => {
      // A dormant module answers 404. The raise will say so when it lands.
    });
  }, [andonPreview, workcentreId, flowlineId]);

  const owned = useMyOwnedAndons(isLeader);
  const andonAppUrl = useStore((s) => s.andonAppUrl);
  const mine = useMemo<NeedsYouVM[]>(
    () => [
      ...(owned.data ?? []).map(
        (a): NeedsYouVM => ({
          key: `andon:${a.id}`,
          kind: 'andon',
          ref: a.ref,
          title: a.title,
          meta: [a.workcentreName, a.state, `${a.ageMinutes} ${t.minT}`].filter(Boolean).join(' · '),
          tone: a.stars >= 3 ? 'danger' : 'warn',
          // A link out only while the Andon app owns the thread.
          ...(andonPreview ? {} : { href: andonHref(andonAppUrl, a.id) }),
          andon: a,
        }),
      ),
      ...needs.items.filter((i) => i.kind !== 'andon'),
    ],
    [owned.data, needs.items, andonAppUrl, andonPreview, t.minT],
  );

  const stationBuffers = useStationBuffers(workcentreId, scopeIds);
  const crew = useCrewToday(tablet);
  const completedText = (stationScore.data?.lines ?? [])
    .map((l) => `${l.short} ${l.target == null ? l.done : `${l.done}/${l.target}`}`)
    .join(' · ');

  const headerTitle = onFloor && screen !== 'job' ? t.productionT : (wcName ?? '—');
  const headerSub = [
    operator?.name.split(' ')[0],
    isLeader ? t.teamLeaderT : null,
    onFloor && screen !== 'job' ? `${t.lastAtT} ${wcName ?? '—'}` : scopeLabel,
  ]
    .filter(Boolean)
    .join(' · ');

  // The raise is a sheet over the tab it was opened from (frame v2.12), so
  // the tab stays current and the bar stays under the scrim.
  const activeTab = screen === 'job' || screen === 'complete' ? null : tab;
  // THE THREAD, with the preview on. The andon sheet's overlay is the same
  // overlay it always was — `useRouteSync` writes /alert/<id> for it and Back
  // closes it — but what it draws is the Andon app's thread rather than the
  // read-only sheet. On a phone it replaces the frame the way the raise does;
  // on a tablet it docks beside the screen that opened it, in the rail's
  // place, so the floor stays in view while an andon is worked.
  const showingAlert = andonPreview && overlay === 'andonDetail' && openAndonId != null;
  const showRail = tablet && screen === 'home' && tab !== 'more' && !showingAlert;
  // The library replaces the whole frame: the operator is reading, and a tab
  // bar under a work instruction is an invitation to lose their place. The
  // thread does too, on a phone, as every chat detail view does. The raise
  // does not — it is a sheet, and the bar sits under its scrim.
  const showTabBar = !tablet && screen !== 'isw' && !showingAlert;
  const alertOnPhone = showingAlert && !tablet;
  // The floating +: phones only, never over a job or a sheet (frame
  // `showPlusFab`). Its dial shuts the moment the disc goes, so it cannot
  // reappear open over whatever comes next.
  const fabVisible = showFab({ tablet, onJob: !scanEnabled, sheetOpen: overlay != null });
  useEffect(() => {
    if (!fabVisible) setFabOpen(false);
  }, [fabVisible]);
  const jobBackLabel = tab === 'pods' ? t.podsT.toLowerCase() : (wcName ?? t.station);

  // ── the body ───────────────────────────────────────────────────────────────
  const body = alertOnPhone ? (
    <AlertScreen alertId={openAndonId} onClose={closeOverlay} tablet={false} onGoToStation={goToStation} />
  ) : restoring ? (
    <div style={{ flex: 1, display: 'grid', placeItems: 'center', minHeight: 200 }}>
      <PodMark size={38} style={{ color: 'var(--accent)', animation: 'ipPulse 1.6s infinite' }} />
    </div>
  ) : showingJob && pod ? (
    <JobScreen
      pod={pod}
      tablet={tablet}
      backLabel={jobBackLabel}
      starting={lifecycle.start.isPending}
      onStart={start}
      onBack={() => {
        setFoundPod(null);
        setScreen('home');
      }}
      onPause={() => (live.data?.startedAt ? openOverlay('pause') : start())}
      onComplete={tryComplete}
      pendingPhoto={pendingPhoto}
      onConfirmPhoto={confirmPhoto}
      onCapturePhoto={(id) => {
        setPhotoCheckId(id);
        fileRef.current?.click();
      }}
      onOpenDocs={() => openOverlay('docs')}
      onOpenIssues={() => {
        setIssuesView('pod');
        openOverlay('issues');
      }}
      onRaise={() => openOverlay('raise')}
      onJumpToStep={jumpToStep}
    />
  ) : screen === 'complete' ? (
    <CompleteScreen
      milestone={milestone}
      done={justDone}
      nextMo={pods[activeIndex]?.mo ?? null}
      onNext={nextPod}
      onHome={() => {
        setFoundPod(null);
        setScreen('home');
      }}
    />
  ) : screen === 'isw' ? (
    <IswLibraryScreen onBack={() => setScreen('home')} />
  ) : tab === 'pods' ? (
    <PodsScreen onOpen={(p) => openPodRow(p)} />
  ) : tab === 'alerts' ? (
    <AlertsScreen floor={onFloor} scopeLabel={scopeLabel} actions={actions} />
  ) : tab === 'andon' ? (
    <AndonScreen floor={onFloor} flowlineIds={scopeIds} scopeLabel={scopeLabel} actions={actions} />
  ) : tab === 'more' ? (
    <MoreScreen
      onOpenSettings={() => openOverlay('settings')}
      onPickStation={() => openOverlay('station')}
      onOpenAlerts={() => setTab('alerts')}
      // The notifications sheet is about pushes, which only the preview
      // sends; a row to a sheet that is not mounted would be a dead tap.
      {...(andonPreview ? { onOpenNotifications: () => openOverlay('notifications') } : {})}
      onOpenBufferBoards={() => window.open('/odoo/buffer-boards', '_blank', 'noopener')}
      onOpenLibrary={() => setScreen('isw')}
      onOpenCrew={() => openOverlay('crew')}
      onSignOut={() => openOverlay('settings')}
    />
  ) : onFloor ? (
    <LeaderHome
      tablet={tablet}
      flowlineIds={scopeIds}
      scopeLabel={scopeLabel}
      onDrillTo={goToStation}
      onOpenAlerts={() => setTab('alerts')}
      onOpenCrew={() => openOverlay('crew')}
      onPickFlowline={(id) => pickScope(flowlineId === id ? null : id)}
    />
  ) : (
    <HomeScreen
      tablet={tablet}
      drilled={drilled}
      onBackToFloor={() => setLeaderView('floor')}
      onOpenPod={openPodRow}
      onOpenPods={openPods}
      onOpenAndons={() => setTab('andon')}
    />
  );

  return (
    <div
      style={{
        // Less the staging strip, which is 0px everywhere but staging (App).
        height: 'calc(100dvh - var(--sf-banner, 0px))',
        display: 'flex',
        flexDirection: 'column',
        background: 'var(--bg)',
        color: 'var(--ink)',
        overflow: 'hidden',
        position: 'relative',
      }}
    >
      {/* On a phone the status chrome gets its own slim row, as it always
          has: beside the station name it pushed "FL - Plumbing" down to
          "FL - P…", and a title that cannot be read is not a header. */}
      {screen !== 'isw' && !tablet && (
        <div
          style={{
            flex: 'none',
            height: 32,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '0 16px',
            background: 'var(--surface)',
          }}
        >
          <span className="numeric" style={{ font: "600 12px 'Space Grotesk',sans-serif" }}>
            {new Date().toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <OfflinePill />
          </span>
        </div>
      )}
      {/* The thread carries its own header on a phone — the andon's number,
          its stars and the way back — and the station header over it would be
          a second title for a screen that is not about the station. */}
      {screen !== 'isw' && !alertOnPhone && (
        <AppHeader
          title={headerTitle}
          sub={headerSub}
          tablet={tablet}
          scopeOptions={scopeOptions}
          scopeValue={flowlineId}
          onScope={pickScope}
          score={headerScore ?? null}
          onIdentity={() => openOverlay('settings')}
          onScan={() => openOverlay('camera')}
          onFind={() => openOverlay('search')}
          trailing={
            tablet ? <OfflinePill size="board" /> : null
          }
        />
      )}

      {lastError && (
        <div style={{ flex: 'none', padding: '10px 16px 0' }}>
          <Banner tone="danger" action={{ label: t.clear, onClick: clearError }}>
            {lastError.msg}
          </Banner>
        </div>
      )}

      <div style={{ flex: 1, minHeight: 0, display: 'flex' }}>
        {tablet && (
          <SideRail tab={activeTab} onTab={setTab} onRaise={() => openOverlay('raise')} badges={badges} />
        )}

        <main
          style={{
            flex: 1,
            minWidth: 0,
            minHeight: 0,
            // The library and the thread scroll inside themselves: the thread
            // has to keep its composer on the floor of the screen.
            overflowY: screen === 'isw' || alertOnPhone ? 'hidden' : 'auto',
            // Never sideways. Anything wider than the column truncates or
            // scrolls inside its own row; the page itself does not move.
            overflowX: 'hidden',
            padding: screen === 'isw' || alertOnPhone ? 0 : 16,
            display: 'flex',
            flexDirection: 'column',
            gap: 14,
          }}
        >
          {body}
          {/* Room under the last row for the floating + (frame template
              L446): without it the disc sits on the bottom card's button. */}
          {fabVisible && <div aria-hidden="true" style={{ flex: 'none', height: 72 }} />}
        </main>

        {showingAlert && tablet && <AlertScreen alertId={openAndonId} onClose={closeOverlay} tablet onGoToStation={goToStation} />}

        {showRail && (
          <RightRail
            isLeader={isLeader}
            mine={mine}
            onOpenNeedsYouItem={openNeedsYouItem}
            stationName={wcName ?? '—'}
            buffers={stationBuffers}
            completedCount={stationScore.data?.done ?? 0}
            completedText={completedText || '—'}
            // Who is on now, once each — a split shift is two attendance rows.
            crew={[...new Map((crew.data ?? []).filter((c) => c.open).map((c) => [c.employeeId, { id: c.employeeId, name: c.name }])).values()]}
            onOpenCrew={() => openOverlay('crew')}
          />
        )}
      </div>

      {showTabBar && <TabBar tab={activeTab} onTab={setTab} badges={badges} />}

      {fabVisible && <PlusFab open={fabOpen} onToggle={() => setFabOpen((v) => !v)} onChoose={onChooseFab} />}

      <Toast />

      {/* One hidden input serves every photo check. */}
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        capture="environment"
        hidden
        onChange={(e) => {
          void onPhotoPicked(e.target.files?.[0]);
          e.target.value = '';
        }}
      />

      <SettingsSheet open={overlay === 'settings'} onClose={closeOverlay} onPickStation={() => openOverlay('station')} />
      {/* Closing the picker goes back to Settings, which is where it is
          opened from — except on the raise, which opens it on its own behalf
          when the device has no station and wants the operator back on the
          raise, not in Settings, once they have picked one. */}
      <StationPickerSheet open={overlay === 'station'} onClose={() => (screen === 'raise' ? closeOverlay() : openOverlay('settings'))} />
      <PodFinderSheet open={overlay === 'search'} onClose={closeOverlay} onPick={pickFound} />
      {/* The viewfinder goes the moment it has read something, not once the
          pod has been looked up: a dead camera over a sheet that is waiting
          on the network looks like a scan that was ignored, and a code that
          is not at this station is told as a toast on the screen behind. */}
      <CameraScanner
        open={overlay === 'camera'}
        onClose={closeOverlay}
        onCode={(code) => {
          closeOverlay();
          void onScan(code);
        }}
        onTypeInstead={() => openOverlay('search')}
      />
      <RaiseSheet open={overlay === 'raise'} pod={pod} onClose={closeOverlay} onChoose={onChooseRaise} />
      <AndonHandoffSheet open={overlay === 'andon'} moId={handoffMoId} onClose={closeOverlay} />
      <AlertFormSheet
        open={overlay === 'form'}
        kind="quality"
        pod={pod}
        onClose={closeOverlay}
        onRaised={(r) => {
          setRaised(r);
          openOverlay('raised');
        }}
      />
      <RaisedSheet open={overlay === 'raised'} raised={raised} kind="quality" onClose={closeOverlay} />
      <IssuesSheet
        open={overlay === 'issues'}
        pod={pod}
        initialView={issuesView}
        onClose={closeOverlay}
        onOpenAlert={(id) => {
          setQualityFrom('issues');
          openQualityAlert(id);
        }}
      />
      <QualitySheet
        open={overlay === 'quality'}
        alertId={openAlertId}
        onClose={() => {
          if (qualityFrom === 'issues') {
            setQualityFrom(null);
            openOverlay('issues');
          } else {
            closeOverlay();
          }
        }}
        onRaiseAndon={raiseAndonOn}
      />
      {/* The read-only sheet, for a session the merge has not reached: with
          the preview on the same overlay draws the thread instead. */}
      <AndonSheet open={overlay === 'andonDetail' && !andonPreview} andonId={openAndonId} onClose={closeOverlay} onGoToStation={goToStation} />
      <CrewSheet open={overlay === 'crew'} onClose={closeOverlay} />

      {/* The preview's own surfaces. Not mounted at all without it: an
          overlay that cannot be opened and a banner about pushes the app
          cannot send have no business in a session the merge has not
          reached. */}
      {andonPreview && (
        <>
          {/* THE RAISE, as one sheet over the tab (frame v2.12 template
              L584-609). It is still the `raise` screen state and /raise in
              the address bar; it yields to any overlay opened from inside
              it — the station picker — and comes back when that closes. */}
          <RaiseSheetLive open={screen === 'raise' && overlay == null} onClose={() => setScreen('home')} />
          <NotificationSheet open={overlay === 'notifications'} onClose={closeOverlay} />
          <PushBanner />
          {/* The paper, for the person who just closed an andon. A counter on
              the andon slice, so it fires on the change rather than on a
              state somebody has to remember to clear. */}
          <Confetti fire={celebrate} />
        </>
      )}

      <Sheet open={overlay === 'pause'} onClose={closeOverlay} title={t.pausePod}>
        <p style={{ font: '500 12.5px Manrope,sans-serif', color: 'var(--sub)', marginTop: 0 }}>{t.chooseReasonLogged}</p>
        <ReasonList
          items={(lossReasons.data ?? []).map((l) => ({ id: l.id, label: l.name, dot: l.dot }))}
          onPick={(item) => {
            setPauseReason(String(item.label));
            lifecycle.pause.mutate(String(item.label), { onSuccess: closeOverlay });
          }}
        />
      </Sheet>

      <Sheet
        open={overlay === 'quick'}
        onClose={closeOverlay}
        title={t.thatWasQuick}
        footer={
          <Button
            variant="primary"
            disabled={!quickReason}
            pending={lifecycle.finish.isPending}
            onClick={() => quickReason && completeNow({ reason: quickReason, ...(claimedStart ? { claimedStart } : {}) })}
          >
            {t.confirmComplete}
          </Button>
        }
      >
        <p style={{ font: '500 12.5px Manrope,sans-serif', color: 'var(--sub)', marginTop: 0 }}>{t.underHalf}</p>
        <SectionLabel>{t.actualStart}</SectionLabel>
        <input
          type="time"
          value={claimedStart}
          onChange={(e) => setClaimedStart(e.target.value)}
          aria-label={t.actualStart}
          style={{
            height: 46,
            width: '100%',
            borderRadius: 12,
            background: 'var(--surface2)',
            border: '1.5px solid var(--line)',
            padding: '0 12px',
            font: "600 15px 'Space Grotesk',sans-serif",
            color: 'var(--ink)',
            marginBottom: 14,
          }}
        />
        <SectionLabel>{t.reasonT}</SectionLabel>
        <ReasonList items={QUICK_REASONS.map((r) => ({ id: r, label: r }))} selectedId={quickReason} onPick={(i) => setQuickReason(String(i.label))} />
      </Sheet>

      <Sheet open={overlay === 'docs'} onClose={closeOverlay} title={t.plansDocs}>
        <PodDocs pod={pod} wcName={wcName} />
      </Sheet>
    </div>
  );
}

function CompleteScreen({
  milestone,
  done,
  nextMo,
  onNext,
  onHome,
}: {
  milestone: Milestone | null;
  done: { mo: string; proj: string; nextStep: string | null } | null;
  nextMo: string | null;
  onNext: () => void;
  onHome: () => void;
}) {
  const t = useT();
  return (
    <div
      style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px 24px 24px',
        textAlign: 'center',
        animation: 'ipFade .3s',
        maxWidth: 460,
        margin: '0 auto',
        width: '100%',
      }}
    >
      <div style={{ position: 'relative', marginBottom: 20 }}>
        <div
          style={{
            width: 96,
            height: 96,
            borderRadius: '50%',
            background: 'var(--pos)',
            display: 'grid',
            placeItems: 'center',
            animation: 'ipPop .5s cubic-bezier(.2,.7,.3,1.4)',
          }}
        >
          <span style={{ font: '700 46px system-ui', color: '#fff', lineHeight: 1 }}>✓</span>
        </div>
        <span
          aria-hidden="true"
          style={{ position: 'absolute', inset: 0, borderRadius: '50%', border: '3px solid var(--pos)', animation: 'ipRing .85s ease-out .12s both' }}
        />
      </div>
      <h1 style={{ font: "700 25px 'Space Grotesk',sans-serif", margin: '0 0 8px' }}>
        {done ? `${done.mo} ${t.done.toLowerCase()}` : t.completeJobT}
      </h1>
      <p style={{ font: '500 13px Manrope,sans-serif', color: 'var(--sub)', maxWidth: 280, margin: '0 0 28px' }}>
        {done?.nextStep ? `${t.thisPod}${done.proj ? ` — ${done.proj}` : ''} ${t.isReadyFor} ${done.nextStep}.` : t.podMovedOn}
      </p>
      {milestone && (
        <div
          style={{
            width: '100%',
            background: 'var(--soft)',
            border: '1.5px solid var(--accent)',
            borderRadius: 16,
            padding: 14,
            marginBottom: 18,
            animation: 'ipRise .35s ease-out',
          }}
        >
          <div style={{ font: "700 14px 'Space Grotesk',sans-serif" }}>🎉 {milestone.title}</div>
          <div style={{ font: '500 11.5px Manrope,sans-serif', color: 'var(--sub)', marginTop: 3 }}>{milestone.sub}</div>
        </div>
      )}
      <div style={{ width: '100%', display: 'grid', gap: 10 }}>
        <Button variant="primary" size="xl" onClick={onNext}>
          {nextMo ? `${t.nextPodT} → ${nextMo}` : `${t.nextPodT} →`}
        </Button>
        <Button variant="soft" size="md" onClick={onHome}>
          {t.backToStation}
        </Button>
      </div>
    </div>
  );
}
